import React from 'react';
import { ColorField } from '@prismicio/types';
import styled from 'styled-components';
import Colors from '@components/shared/Colors';
import { TEXT_STYLES, TextVariant, Typography, TYPOGRAPHY_MOBILE_BREAKPOINT } from '@components/shared/design/Typography';
import Text from '@components/shared/Text';
import { PageSlice, SliceCaseStudySectionDetails, SliceCaseStudySectionItem } from '@contracts';
import useDesktop from './useDesktop';

interface Props {
  slice: PageSlice;
  className?: string;
  vitalsFonts?: boolean;
}

const reorderLinkSpans = (sliceItems: SliceCaseStudySectionItem[], firstSpanName = 'label') => {
  sliceItems.forEach((sliceItem) => {
    const linkField = sliceItem.link?.richText;
    if (linkField) {
      linkField.forEach((linkFieldItem) => {
        if (linkFieldItem.spans && linkFieldItem.spans.length > 0) {
          linkFieldItem.spans.sort((a, b) => {
            if (a.type === firstSpanName) return -1;
            if (b.type === firstSpanName) return 1;
            return 0;
          });
        }
      });
    }
  });
};

const CaseStudySection: React.FC<Props> = ({ slice, className, vitalsFonts = false }) => {
  const sliceDetails = slice.primary as SliceCaseStudySectionDetails;
  const sliceItems = slice.items as SliceCaseStudySectionItem[];
  const mobile = !useDesktop(TYPOGRAPHY_MOBILE_BREAKPOINT);

  if (sliceItems && sliceItems.length > 0) {
    reorderLinkSpans(sliceItems);
  }

  return (
    <Container
      className={className}
      $backgroundColor={sliceDetails?.background_color}
      $padding={sliceDetails.padding?.text}
      $rowGap={sliceDetails.rowGap?.text}
    >
      <SectionHeading $mobile={mobile} $vitalsFonts={vitalsFonts}>
        {sliceDetails.section_title.text}
      </SectionHeading>
      {sliceItems && (
        <ItemsWrap>
          {sliceItems.map((article, index: number) => (
            <ArticleWrap key={index}>
              <Typography $variant={TextVariant.BodyBoldLG}>{article.research_title.text}</Typography>
              <Researcher>{article.researcher?.text}</Researcher>
              <ArticleDescription fontSize="14px" fontSizeMobile="14px" richText={article.research_description?.richText} />
              <LinkText
                richText={article.link_richtext?.richText || article.link?.richText}
                textColor={Colors.lilac}
                padding="0"
                linkTargetBlank={true}
              />
            </ArticleWrap>
          ))}
        </ItemsWrap>
      )}
    </Container>
  );
};

export default CaseStudySection;

interface StyleProps {
  $backgroundColor?: ColorField;
  $padding?: string;
  $rowGap?: string;
  $textColor?: ColorField;
  $mobile?: boolean;
  $vitalsFonts?: boolean;
}

const Container = styled.div<StyleProps>`
  padding: ${(props) => props.$padding};
  background: ${(props) => props.$backgroundColor};
  color: ${Colors.navyBlue};
  display: flex;
  flex-direction: column;
  align-items: center;
  row-gap: ${(props) => props.$rowGap || '56px'};
`;

const SectionHeading = styled.h3<StyleProps>`
  ${(props) => {
    if (props.$vitalsFonts) {
      return props.$mobile ? TEXT_STYLES[TextVariant.UpperText4] : TEXT_STYLES[TextVariant.UpperText3];
    }
    return props.$mobile ? TEXT_STYLES[TextVariant.Heading5] : TEXT_STYLES[TextVariant.Heading4];
  }}
  text-align: center;
`;

const ItemsWrap = styled.div`
  columns: 2 400px;
  column-gap: 56px;
  max-width: 900px;
  margin: auto;
`;

const ArticleWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 400px;
  max-width: 100%;
  padding-bottom: 60px;
  margin: auto;
  break-inside: avoid-column;

  @media (max-width: 1024px) {
    padding-bottom: 30px;
    padding-inline: 30px;
    width: 100%;
  }
`;

const Researcher = styled.p`
  font-family: BrownStd;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0;
  text-align: left;

  color: ${Colors.navyBlue};
  opacity: 0.5;

  @media (max-width: 1024px) {
    font-size: 12px;
    line-height: 18px;
  }
`;

const ArticleDescription = styled(Text)`
  @media (max-width: 767px) {
    .text-section {
      padding: 0 !important;
    }
  }
`;

const LinkText = styled(Text)`
  padding: 0;
  & > a {
    color: ${Colors.lilac};
  }
`;
